// ============================================================
// LinkRelationship.tsx
// Round 2 — how two linked cases are connected (Same accident / Related /
// Other), shown the SAME way wherever linked cases are listed: the case
// page's Linked cases section and the client page's "Linked to" list.
//
//   • LinkRelationshipBadge   — the badge: SameAccidentPill / a neutral
//     "Related" / "Other" tag, or the amber "Please confirm" chip for links
//     made before relationships existed (relationshipConfirmed === false).
//   • LinkRelationshipControl — badge + the one-click choices (always shown
//     while unconfirmed; behind "Change" otherwise). Saves via
//     updateCaseLinkRelationshipApi, then PUBLISHES the change so every
//     other list of that link updates at once (the server's live event is
//     echo-suppressed for the actor, so their own other views need this).
//   • subscribeLinkRelationship / applyLinkRelationship — the in-app
//     channel; applyLinkRelationship also patches the store's open case
//     (currentCase.caseLinks) when it's either side of the link.
// ============================================================
import React, { useState } from 'react'
import { updateCaseLinkRelationshipApi } from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'
import type { CaseLinkRelationship, CaseLinkSummary } from '../../types/case.types'
import { SameAccidentPill } from './AccidentGroupPanel'

export const RELATIONSHIPS: { key: CaseLinkRelationship; label: string; desc: string }[] = [
  { key: 'SAME_ACCIDENT', label: 'Same accident', desc: 'Both claims arise from the same accident. Groups the cases and shows the shared accident date and place.' },
  { key: 'RELATED', label: 'Related', desc: 'Connected matters, e.g. co-defendants or a follow-on claim.' },
  { key: 'OTHER', label: 'Other', desc: 'Any other connection.' },
]
export const relLabel = (r: CaseLinkRelationship | undefined): string => RELATIONSHIPS.find((x) => x.key === r)?.label ?? 'Related'

// ── In-app change channel ───────────────────────────────────
export interface LinkRelationshipChange { caseId: string; targetCaseId: string; relationship: CaseLinkRelationship }
type Listener = (c: LinkRelationshipChange) => void
const listeners = new Set<Listener>()
export const subscribeLinkRelationship = (fn: Listener): (() => void) => {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}
// The link entry in `links` for the OTHER side of `c` as seen from `fromCaseId`
// gets the new relationship (and is confirmed). Unchanged array if absent.
export const patchLinks = <T extends Pick<CaseLinkSummary, 'caseId' | 'relationship' | 'relationshipConfirmed'>>(
  links: T[], fromCaseId: string, c: LinkRelationshipChange,
): T[] => {
  const other = fromCaseId === c.caseId ? c.targetCaseId : fromCaseId === c.targetCaseId ? c.caseId : null
  if (!other || !links.some((l) => l.caseId === other)) return links
  return links.map((l) => (l.caseId === other ? { ...l, relationship: c.relationship, relationshipConfirmed: true } : l))
}
export const applyLinkRelationship = (c: LinkRelationshipChange): void => {
  // The open case (case page / hero / overview read currentCase.caseLinks).
  // caseLinks is server-derived and not part of the auto-save payload, so
  // patching it here never triggers a save.
  const cur = useCasevoStore.getState().currentCase
  if (cur?.caseLinks) {
    const next = patchLinks(cur.caseLinks, cur.id, c)
    if (next !== cur.caseLinks) useCasevoStore.setState({ currentCase: { ...cur, caseLinks: next } })
  }
  listeners.forEach((fn) => fn(c))
}

// ── Badge ───────────────────────────────────────────────────
export const LinkRelationshipBadge: React.FC<{ relationship?: CaseLinkRelationship; confirmed?: boolean }> = ({ relationship, confirmed }) =>
  confirmed === false ? (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.06em] px-2 py-[3px] rounded-[4px] bg-[#fbf1dc] text-[#7a5d10] dark:bg-[rgba(224,182,74,0.14)] dark:text-[#e0b64a]"
      title="Linked before relationships existed — choose how these cases are connected">
      Please confirm
    </span>
  ) : relationship === 'SAME_ACCIDENT' ? (
    <SameAccidentPill />
  ) : (
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] px-2 py-[3px] rounded-[4px] bg-casevo-input text-casevo-text-soft border border-casevo-border-soft">{relLabel(relationship)}</span>
  )

// ── Badge + choices ─────────────────────────────────────────
// `caseId` = the case the link is listed FROM; `link.caseId` = the other one.
// Locked (the viewer can't open the other case) → badge only. `onChanged`
// runs after the save + publish (the case page re-fetches for the shared
// accident details / accident group).
export const LinkRelationshipControl: React.FC<{
  caseId: string
  link: Pick<CaseLinkSummary, 'caseId' | 'relationship' | 'relationshipConfirmed'>
  locked?: boolean
  onChanged?: (rel: CaseLinkRelationship) => void | Promise<void>
}> = ({ caseId, link, locked, onChanged }) => {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const unconfirmed = link.relationshipConfirmed === false

  const choose = async (rel: CaseLinkRelationship): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await updateCaseLinkRelationshipApi(caseId, link.caseId, rel)
      applyLinkRelationship({ caseId, targetCaseId: link.caseId, relationship: rel })
      setEditing(false)
      await onChanged?.(rel)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t update the link.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* Stops clicks / keys reaching a clickable card around it. */}
      <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <LinkRelationshipBadge relationship={link.relationship} confirmed={link.relationshipConfirmed} />
        {(unconfirmed || editing) && !locked ? (
          RELATIONSHIPS.map((r) => (
            <button key={r.key} type="button" disabled={busy} title={r.desc}
              onClick={() => { void choose(r.key) }}
              className={`h-[24px] px-2 rounded-[6px] text-[11.5px] font-semibold border focus:outline-none casevo-focus disabled:opacity-50 ${!unconfirmed && link.relationship === r.key ? 'border-casevo-mid text-casevo-brand bg-casevo-mint' : 'border-casevo-border text-casevo-forest hover:border-casevo-mid'}`}>
              {r.label}
            </button>
          ))
        ) : !locked && (
          <button type="button" onClick={() => setEditing(true)}
            className="text-[11.5px] text-casevo-text-soft hover:text-casevo-brand underline underline-offset-2 focus:outline-none casevo-focus">
            Change
          </button>
        )}
        {editing && (
          <button type="button" onClick={() => setEditing(false)} className="text-[11.5px] text-casevo-text-soft hover:text-casevo-forest focus:outline-none casevo-focus">Cancel</button>
        )}
      </div>
      {error && <p role="alert" className="m-0 mt-1 text-[12px] text-casevo-danger">{error}</p>}
    </>
  )
}
